import * as RadixDialog from "@radix-ui/react-dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { config, useTransition } from "@react-spring/web";
import { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import ModalContainer from "./ModalContainer";

import { useIsMobile } from "../../../hooks/customDeviceDetect";
import type {
  ModalContainerProps,
  ModalOptions,
  ModalProps,
} from "./ModalStack.types";

const StackWrapper = styled.div<ModalOptions>`
  z-index: 5;
  position: fixed;
  bottom: ${({ isMobile }) => (isMobile ? 0 : "50%")};
  left: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Stack = ({
  stack,
  closeModal,
}: {
  stack: ModalProps[];
  closeModal: () => void;
}) => {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(!!stack.length);
  const modalIndex = (item: ModalProps) =>
    stack.length - stack.indexOf(item) - 1;
  const handleTransform = (
    prop: keyof Exclude<ModalOptions["transform"], undefined | boolean>,
    item: ModalProps,
    falseValue: number,
    defaultValue: number,
  ) => {
    const transform = item.options?.transform?.[prop];
    if (typeof transform === "function") return transform(modalIndex(item));
    if (transform === false || item.options?.transform === false)
      return falseValue;
    return defaultValue;
  };
  const x = (item: ModalProps, update = false) => {
    if (update) return handleTransform("x", item, 0, 0);
    if (item.options?.enterFrom === "right") return 200;
    if (item.options?.enterFrom === "left") return -200;
    return 0;
  };
  const y = (item: ModalProps, update = false) => {
    if (update) return handleTransform("y", item, 0, modalIndex(item) * -40);

    if (item.options?.swipe) return 1000;
    if (item.options?.enterFrom === "top") return -100;
    if (item.options?.enterFrom === "bottom") return 100;
    if (item.options?.enterFrom) return 0;
    return 30;
  };
  const scale = (item: ModalProps, update = false) => {
    if (update)
      return handleTransform("scale", item, 1, 1 - modalIndex(item) / 7);
    if (item.options?.enterFrom) return 0.9;
    return 1.1;
  };
  const away = (item: ModalProps) => ({
    opacity: 0,
    shading: 1,
    y: y(item),
    x: x(item),
    scale: scale(item),
    pointerEvents: "none",
  });
  const modalStackTransition = useTransition(stack, {
    from: (item) => away(item),
    leave: (item) => away(item),
    onStart: () => {
      setOpen(true);
    },
    // onRest: (item) => {
    //   if (item.finished && stack.length === 0) {
    //     setOpen(false);
    //   }
    // },
    enter: {
      opacity: 1,
      shading: 1,
      y: 0,
      x: 0,
      scale: 1,
      pointerEvents: "auto",
    },
    update: (item) => [
      {
        shading: Math.min(1 / (modalIndex(item) + 1), 1),
        y: y(item, true),
        x: x(item, true),
        scale: scale(item, true),
      },
    ],
    config: config.gentle,
  });

  useEffect(() => {
    // Introduce a timeout to delay the execution
    const timer = setTimeout(() => {
      if (!stack.length) {
        setOpen(false);
      }
    }, 900); // Delay of 1000 milliseconds (1 second)

    // Cleanup function to clear the timeout if the component unmounts or the dependencies change
    return () => clearTimeout(timer);
  }, [stack.length]); // Dependency on stack.length

  return (
    <RadixDialog.Root open={open}>
      <RadixDialog.Portal>
        {/* <RadixDialog.Overlay /> */}
        <RadixDialog.Content className="z-[9999999]">
          <VisuallyHidden asChild>
            <RadixDialog.Title>Modal Dialog</RadixDialog.Title>
          </VisuallyHidden>
          <StackWrapper isMobile={isMobile} id="modal-stack">
            {modalStackTransition((style, item) => (
              <ModalContainer
                item={item}
                style={style as ModalContainerProps["style"]}
                closeModal={closeModal}
                index={modalIndex(item)}
              />
            ))}
          </StackWrapper>
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
};
export default Stack;
